import React, { useEffect, useMemo } from "react";
import { Stack, Typography, Box, Divider, Alert } from "@mui/material";
import { Formik, Form } from "formik";
import AppModal from "../common/AppModal";
import FormSelectField from "../common/FormSelectField";
import FormSearchSelect from "../common/FormSearchSelect";
import AppButton from "../buttons/AppButton";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { saveUserDefaults } from "../../redux/actions/settingsActions";

// Dependent actions to fetch lookup data
import { getCategories } from "../../redux/actions/categoryActions";
import { getBrands } from "../../redux/actions/brandActions";
import { getTaxCodes } from "../../redux/actions/taxCodeActions";
import { getQuantityUnits } from "../../redux/actions/quantityUnitActions";
import { getPackageUnits } from "../../redux/actions/packageUnitActions";
import { getPaymentMethods } from "../../redux/actions/paymentMethodActions";

import { ItemType } from "../../shared/enums/ItemType";
import { UserDefaults } from "../../shared/interfaces/UserDefaults";

const ITEM_TYPES = [
  { value: ItemType.PRODUCT, label: "Product (Tangible)" },
  { value: ItemType.RAW_MATERIAL, label: "Raw Material (Tangible)" },
  { value: ItemType.SERVICE, label: "Service (Intangible)" },
];

// Strictly type the lookup data to eliminate 'any'
interface LookupItem {
  id?: string | number;
  code?: string;
  name: string;
}

interface FormOption {
  value: string | number;
  label: string;
}

const UserDefaultsModal: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { userDefaults } = useAppSelector((state) => state.settings);

  // Fetch Lookups
  const { items: categories } = useAppSelector((state) => state.category);
  const { items: brands } = useAppSelector((state) => state.brand);
  const { items: paymentMethods } = useAppSelector(
    (state) => state.paymentMethod,
  );
  const { items: taxCodes } = useAppSelector((state) => state.taxCode);
  const { items: quantityUnits } = useAppSelector(
    (state) => state.quantityUnit,
  );
  const { items: packageUnits } = useAppSelector((state) => state.packageUnit);

  useEffect(() => {
    if (open) {
      if (!categories.length) dispatch(getCategories({ size: 100 }));
      if (!brands.length) dispatch(getBrands({ size: 100 }));
      if (!paymentMethods.length) dispatch(getPaymentMethods({ size: 50 }));
      if (!taxCodes.length) dispatch(getTaxCodes({ size: 50 }));
      if (!quantityUnits.length) dispatch(getQuantityUnits({ size: 50 }));
      if (!packageUnits.length) dispatch(getPackageUnits({ size: 50 }));
    }
  }, [
    brands.length,
    categories.length,
    dispatch,
    open,
    packageUnits.length,
    paymentMethods.length,
    quantityUnits.length,
    taxCodes.length,
  ]);

  // Safely map data using the LookupItem interface
  const mapToOptions = (data: LookupItem[]): FormOption[] => [
    { value: "", label: "-- None --" },
    ...(data?.map((d) => ({
      value: d.id !== undefined ? d.id : (d.code ?? ""),
      label: d.name,
    })) || []),
  ];

  const options = useMemo(
    () => ({
      categories: mapToOptions(categories),
      brands: mapToOptions(brands),
      pm: mapToOptions(paymentMethods),
      tax: mapToOptions(taxCodes),
      qty: mapToOptions(quantityUnits),
      pkg: mapToOptions(packageUnits),
    }),
    [categories, brands, paymentMethods, taxCodes, quantityUnits, packageUnits],
  );

  // Ensure initialValues never has nulls falling into inputs, causing uncontrolled component errors
  const safeInitialValues = useMemo(() => {
    if (!userDefaults) return {} as UserDefaults;
    const safeDefaults = { ...userDefaults };
    Object.keys(safeDefaults).forEach((key) => {
      const k = key as keyof UserDefaults;
      if (safeDefaults[k] === null || safeDefaults[k] === undefined) {
        safeDefaults[k] = "";
      }
    });
    return safeDefaults;
  }, [userDefaults]);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Operational Defaults"
      width="md"
    >
      <Alert severity="info" sx={{ mb: 3 }}>
        These values will be pre-filled automatically when creating items or
        processing sales to save time. These settings are specific to this
        browser/device.
      </Alert>
      <Formik
        initialValues={safeInitialValues}
        enableReinitialize={true}
        onSubmit={(values) => {
          // Convert empty strings ('') back to null to properly save "None" selections
          const cleanedValues = Object.entries(values).reduce(
            (acc, [key, value]) => {
              acc[key as keyof UserDefaults] = value === "" ? null : value;
              return acc;
            },
            {} as Partial<UserDefaults>,
          );

          dispatch(saveUserDefaults(cleanedValues as UserDefaults));
          onClose();
        }}
      >
        <Form>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                POS Checkout Defaults
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mt: 1,
                }}
              >
                <FormSelectField
                  name="paymentMode"
                  label="Default Payment Mode"
                  options={options.pm}
                />
              </Box>
            </Box>
            <Divider />
            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                Item Creation Defaults
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mt: 1,
                }}
              >
                <FormSelectField
                  name="itemType"
                  label="Default Item Type"
                  options={ITEM_TYPES}
                />
                <FormSearchSelect
                  name="taxCode"
                  label="Default Tax Group"
                  options={options.tax}
                />
                <FormSearchSelect
                  name="quantityUnit"
                  label="Default Qty Unit"
                  options={options.qty}
                />
                <FormSearchSelect
                  name="packageUnit"
                  label="Default Pkg Unit"
                  options={options.pkg}
                />
                <FormSearchSelect
                  name="categoryId"
                  label="Default Category"
                  options={options.categories}
                />
                <FormSearchSelect
                  name="brandId"
                  label="Default Brand"
                  options={options.brands}
                />
              </Box>
            </Box>
            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "flex-end" }}
            >
              <AppButton variant="outlined" color="error" onClick={onClose}>
                Cancel
              </AppButton>
              <AppButton variant="contained" color="primary" type="submit">
                Save Defaults
              </AppButton>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </AppModal>
  );
};

export default UserDefaultsModal;
